import React from 'react';
import {
  ArchiveBoxIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CircleStackIcon,
  ClockIcon,
  CubeIcon,
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ListBulletIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  TableCellsIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { Badge, Button, Spinner } from '../ui';
import { DataTable, type Column } from '../ui/data-table';
import {
  useWorkspaceSemanticCorrectionProposals,
  useWorkspaceSemanticRuntimePacks,
  type SemanticContextPackDeltaSummary,
  type SemanticContextOverviewResponse,
  type SemanticContextPackSummary,
  type WorkspaceSemanticCorrectionProposal,
} from '../../hooks/useWorkspaceSemanticContext';
import type { StructuredAnalyticsDraft } from './structuredAnalyticsConfig';

export interface WorkspaceContextPacksSectionProps {
  workspaceId?: number | null;
  draft: StructuredAnalyticsDraft;
  semanticContext?: SemanticContextOverviewResponse;
  isCompiling?: boolean;
  compileError?: string | null;
  compileSuccess?: string | null;
  publishingPackId?: number | null;
  onPublishPack?: (packId: number) => void | Promise<void>;
}

type PackStaleInfo = {
  isStale: boolean;
  proposalIds: number[];
  catalogRequestIds: number[];
};
type ResolvedPackDelta = {
  proposalIds: number[];
  proposals: WorkspaceSemanticCorrectionProposal[];
  missingProposalIds: number[];
  catalogRequestIds: number[];
};
type RuntimeChangeRow = {
  id: string;
  changeType: string;
  section: string;
  label: string;
  fields: string[];
  before: string | null;
  after: string | null;
};

function formatDate(value?: string | null): string {
  if (!value) return 'Not reported';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function formatStatusLabel(value?: string | null): string {
  if (!value) return 'Unavailable';
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getDeltaChanges(delta?: SemanticContextPackDeltaSummary | null): NonNullable<SemanticContextPackDeltaSummary['changes']> {
  const changes = delta?.changes;
  return Array.isArray(changes) ? changes : [];
}

function getDeltaTotal(delta?: SemanticContextPackDeltaSummary | null): number {
  if (!delta) return 0;
  return typeof delta.total_changes === 'number' && Number.isFinite(delta.total_changes)
    ? delta.total_changes
    : getDeltaChanges(delta).length;
}

function getDeltaChangeFields(change: { fields?: unknown }): string[] {
  return Array.isArray(change.fields)
    ? change.fields.map((field) => String(field)).filter(Boolean)
    : [];
}

function buildRuntimeChangeRows(delta?: SemanticContextPackDeltaSummary | null): RuntimeChangeRow[] {
  return getDeltaChanges(delta).map((change, index) => {
    const changeRecord = getRecord(change);
    const changeName = typeof changeRecord.name === 'string' ? changeRecord.name : null;
    const changeType = String(change.change_type ?? 'changed');
    const section = humanizeToken(change.section ?? 'Runtime version');
    const label = String(change.label ?? changeName ?? 'Unnamed change');

    return {
      id: `${change.section ?? 'section'}-${change.key ?? change.label ?? index}-${changeType}`,
      changeType,
      section,
      label,
      fields: getDeltaChangeFields(change),
      before: change.before || null,
      after: change.after || null,
    };
  });
}

function humanizeToken(value: string | number | null | undefined): string {
  return String(value ?? '').replace(/_/g, ' ');
}

function formatCorrectionValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Not set';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  try {
    const serialized = JSON.stringify(value);
    return serialized.length > 180 ? `${serialized.slice(0, 177)}...` : serialized;
  } catch {
    return String(value);
  }
}

function statusVariant(status: string): React.ComponentProps<typeof Badge>['variant'] {
  if (status === 'pending' || status === 'needs_clarification') return 'warning';
  if (status === 'rejected') return 'danger';
  if (status.startsWith('applied') || status === 'approved') return 'success';
  return 'secondary';
}

function packBadge(pack?: SemanticContextPackSummary | null) {
  if (!pack) return <Badge variant="secondary">Missing</Badge>;
  if (pack.is_active && getPackStaleInfo(pack).isStale) return <Badge variant="warning">Live, changes pending</Badge>;
  if (pack.is_active) return <Badge variant="success">Live</Badge>;
  if (pack.status === 'candidate') return <Badge variant="warning">Ready to publish</Badge>;
  if (pack.status === 'failed') return <Badge variant="danger">Failed</Badge>;
  return <Badge variant="warning">{formatStatusLabel(pack.status || 'candidate')}</Badge>;
}

function getRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function numberArray(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item));
}

function getPackStaleInfo(pack?: SemanticContextPackSummary | null): PackStaleInfo {
  const validation = getRecord(pack?.validation_summary);
  const lifecycle = getRecord(validation.pack_lifecycle);
  const stale = getRecord(lifecycle.stale_after_corrections);
  return {
    isStale: stale.is_stale === true,
    proposalIds: numberArray(stale.workspace_proposal_ids),
    catalogRequestIds: numberArray(stale.catalog_correction_request_ids),
  };
}

function targetRefSummary(proposal: WorkspaceSemanticCorrectionProposal): string {
  const targetRef = getRecord(proposal.target_ref);
  const entries = Object.entries(targetRef);
  if (entries.length === 0) return 'No target reference';
  return entries.map(([key, value]) => `${key}: ${formatCorrectionValue(value)}`).join(' · ');
}

function resolvePackDelta(
  staleInfo: PackStaleInfo,
  proposals: WorkspaceSemanticCorrectionProposal[],
): ResolvedPackDelta {
  const proposalsById = new Map(proposals.map((proposal) => [proposal.id, proposal]));
  const resolved = staleInfo.proposalIds
    .map((id) => proposalsById.get(id))
    .filter((proposal): proposal is WorkspaceSemanticCorrectionProposal => Boolean(proposal));
  const missingProposalIds = staleInfo.proposalIds.filter((id) => !proposalsById.has(id));

  return {
    proposalIds: staleInfo.proposalIds,
    proposals: resolved,
    missingProposalIds,
    catalogRequestIds: staleInfo.catalogRequestIds,
  };
}

function RuntimeMetricsBar({
  metrics,
}: {
  metrics: Array<{
    label: string;
    value: React.ReactNode;
    detail: string;
    icon: React.ReactNode;
  }>;
}) {
  return (
    <div className="border-t border-gray-200 px-5 py-4 dark:border-dark-border">
      <div className="grid overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface md:grid-cols-4">
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className="flex gap-4 border-b border-gray-200 px-4 py-4 last:border-b-0 dark:border-dark-border md:border-b-0 md:border-r md:last:border-r-0"
          >
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              {metric.icon}
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">{metric.label}</p>
              <p className="mt-1 text-lg font-semibold text-charcoal dark:text-gray-100">{metric.value}</p>
              <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{metric.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PackDeltaRow({ proposal }: { proposal: WorkspaceSemanticCorrectionProposal }) {
  const submittedPack = proposal.active_pack_version_at_submission
    ? `Submitted against live version v${proposal.active_pack_version_at_submission}`
    : 'Submitted before a live version was recorded';

  return (
    <article className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={statusVariant(proposal.status)}>{humanizeToken(proposal.status)}</Badge>
            <Badge variant="secondary">{humanizeToken(proposal.correction_kind)}</Badge>
            <Badge variant="secondary">{humanizeToken(proposal.target_scope)}</Badge>
          </div>
          <h4 className="mt-2 text-sm font-semibold text-charcoal dark:text-gray-100">
            {proposal.user_text || proposal.proposed_value || `Correction proposal #${proposal.id}`}
          </h4>
          <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
            #{proposal.id} · {submittedPack} · {formatDate(proposal.applied_at ?? proposal.updated_at)}
          </p>
          <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
            {proposal.field_path ? `${proposal.field_path} · ` : ''}
            {targetRefSummary(proposal)}
          </p>
        </div>
        {proposal.resulting_pack_id ? (
          <Badge variant="success">Included in pack #{proposal.resulting_pack_id}</Badge>
        ) : (
          <Badge variant="warning">Not in live version</Badge>
        )}
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2/50">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
            Current live value
          </p>
          <p className="mt-2 whitespace-pre-wrap break-words text-xs leading-5 text-charcoal dark:text-gray-100">
            {formatCorrectionValue(proposal.original_value)}
          </p>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-3 dark:border-emerald-900/50 dark:bg-emerald-900/10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
            Pending next pack value
          </p>
          <p className="mt-2 whitespace-pre-wrap break-words text-xs leading-5 text-charcoal dark:text-gray-100">
            {formatCorrectionValue(proposal.proposed_value ?? proposal.proposed_patch)}
          </p>
        </div>
      </div>

      {proposal.reviewer_notes ? (
        <p className="mt-3 text-xs leading-5 text-gray-500 dark:text-gray-400">
          Review notes: {proposal.reviewer_notes}
        </p>
      ) : null}
    </article>
  );
}

function PendingPackDelta({
  delta,
  isLoading,
  isError,
}: {
  delta: ResolvedPackDelta;
  isLoading: boolean;
  isError: boolean;
}) {
  const totalDeltaCount = delta.proposalIds.length + delta.catalogRequestIds.length;

  return (
    <div className="border-t border-gray-200 px-5 py-5 dark:border-dark-border">
      <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4 dark:border-amber-900/40 dark:bg-amber-900/10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-charcoal dark:text-gray-100">Pending pack delta</p>
            <p className="mt-1 max-w-3xl text-xs leading-5 text-amber-800 dark:text-amber-200">
              The live Runtime Context version has approved semantic changes pending. Generate a new version to review
              the changes below, then publish it when ready.
            </p>
          </div>
          <Badge variant="warning">
            {totalDeltaCount} pending change{totalDeltaCount === 1 ? '' : 's'}
          </Badge>
        </div>

        {isLoading ? (
          <div className="mt-4 flex items-center gap-2 text-xs text-amber-800 dark:text-amber-200">
            <Spinner size="sm" />
            Loading correction details...
          </div>
        ) : null}
        {isError ? (
          <div className="mt-4 rounded-lg border border-amber-300 bg-white p-3 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-dark-surface dark:text-amber-200">
            The pack reported stale correction IDs, but the correction proposal API did not return details.
          </div>
        ) : null}

        {delta.proposals.length > 0 ? (
          <div className="mt-4 space-y-3">
            {delta.proposals.map((proposal) => (
              <PackDeltaRow key={proposal.id} proposal={proposal} />
            ))}
          </div>
        ) : null}

        {(delta.missingProposalIds.length > 0 || delta.catalogRequestIds.length > 0) && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3 text-xs leading-5 text-gray-600 dark:border-dark-border dark:bg-dark-surface dark:text-gray-300">
            {delta.missingProposalIds.length > 0 ? (
              <p>
                Workspace proposal details unavailable for{' '}
                {delta.missingProposalIds.map((id) => `#${id}`).join(', ')}.
              </p>
            ) : null}
            {delta.catalogRequestIds.length > 0 ? (
              <p className={delta.missingProposalIds.length > 0 ? 'mt-1' : undefined}>
                Catalog correction request{delta.catalogRequestIds.length === 1 ? '' : 's'}{' '}
                {delta.catalogRequestIds.map((id) => `#${id}`).join(', ')} also changed metadata after the live version.
                The current workspace response exposes the request IDs, not the full catalog before/after values.
              </p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function RuntimeDeltaPanel({
  title,
  delta,
  emptyDetail,
  compact = false,
  maxRows = compact ? 3 : 8,
}: {
  title: string;
  delta?: SemanticContextPackDeltaSummary | null;
  emptyDetail?: string;
  compact?: boolean;
  maxRows?: number;
}) {
  const [showAllRows, setShowAllRows] = React.useState(false);
  const totalChanges = getDeltaTotal(delta);
  if (!delta || totalChanges === 0) {
    if (!emptyDetail) return null;
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500 dark:border-dark-border dark:bg-dark-surface-2/50 dark:text-gray-400">
        {emptyDetail}
      </div>
    );
  }
  const rows = buildRuntimeChangeRows(delta);
  const shownRows = showAllRows ? rows : rows.slice(0, maxRows);

  const countParts = [
    delta.entity_changes ? `${delta.entity_changes} entities` : null,
    delta.metric_changes ? `${delta.metric_changes} metrics` : null,
    delta.relationship_changes ? `${delta.relationship_changes} relationships` : null,
    delta.verified_query_changes ? `${delta.verified_query_changes} verified queries` : null,
    delta.table_changes ? `${delta.table_changes} tables` : null,
  ].filter(Boolean);
  const columns: Column<RuntimeChangeRow>[] = [
    {
      id: 'entity',
      header: 'Entity',
      minWidth: '210px',
      cell: ({ row }) => (
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-surface-2">
            <UserCircleIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-charcoal dark:text-gray-100">{row.label}</p>
            {row.fields.length > 0 ? (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{row.fields.join(', ')}</p>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      id: 'change',
      header: 'Change',
      width: '130px',
      cell: ({ row }) => (
        <div className="flex items-center">
          <Badge variant={row.changeType === 'removed' ? 'danger' : row.changeType === 'added' ? 'success' : 'warning'}>
            {humanizeToken(row.changeType)}
          </Badge>
        </div>
      ),
    },
    {
      id: 'before',
      header: 'Before (live)',
      minWidth: compact ? '260px' : '320px',
      cell: ({ row }) => (
        <p className="rounded-md bg-gray-50 p-3 text-xs leading-5 text-gray-600 dark:bg-dark-surface-2 dark:text-gray-300">
          {row.before || 'Not present'}
        </p>
      ),
    },
    {
      id: 'after',
      header: 'After (draft)',
      minWidth: compact ? '260px' : '320px',
      cell: ({ row }) => (
        <p className="rounded-md bg-emerald-50 p-3 text-xs leading-5 text-gray-700 dark:bg-emerald-900/10 dark:text-gray-200">
          {row.after || 'Not present'}
        </p>
      ),
    },
  ];

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
      <div className="flex flex-col gap-2 border-b border-gray-200 px-4 py-4 dark:border-dark-border sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-charcoal dark:text-gray-100">{title}</p>
          <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
            {countParts.length > 0 ? countParts.join(' · ') : `${totalChanges} changes`}
          </p>
        </div>
        <Badge variant="warning">
          {totalChanges} change{totalChanges === 1 ? '' : 's'}
        </Badge>
      </div>
      {rows.length > 0 ? (
        <>
          <DataTable
            data={shownRows}
            columns={columns}
            getRowId={(row) => row.id}
            compact
            hoverable
            className="[&>div]:rounded-none [&>div]:border-0"
          />
          {(rows.length > shownRows.length || rows.length < totalChanges) ? (
            <div className="border-t border-gray-200 px-4 py-3 dark:border-dark-border">
              {rows.length > shownRows.length ? (
                <Button size="sm" variant="ghost" onClick={() => setShowAllRows(true)}>
                  View all {rows.length} changes
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Showing {rows.length} of {totalChanges} changes.
                </p>
              )}
            </div>
          ) : null}
        </>
      ) : (
        <p className="p-4 text-xs text-gray-500 dark:text-gray-400">
          Detailed change rows are not available for this version yet.
        </p>
      )}
    </div>
  );
}

function RuntimeStatusNode({
  title,
  value,
  detail,
  tone,
  icon,
}: {
  title: string;
  value: React.ReactNode;
  detail: string;
  tone: 'success' | 'warning' | 'brand' | 'neutral';
  icon: React.ReactNode;
}) {
  const toneClasses = {
    success: 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-900/40',
    warning: 'bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-900/40',
    brand: 'bg-eliza-red/10 text-eliza-red border-eliza-red/20 dark:bg-eliza-red/20 dark:text-eliza-red-light dark:border-eliza-red/30',
    neutral: 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-dark-surface-2 dark:text-gray-300 dark:border-dark-border',
  } as const;
  const dotClasses = {
    success: 'bg-emerald-600',
    warning: 'bg-amber-600',
    brand: 'bg-purple-700 dark:bg-purple-300',
    neutral: 'bg-gray-500',
  } as const;

  return (
    <div className="flex min-w-0 flex-1 items-center gap-4 px-5 py-5">
      <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border ${toneClasses[tone]}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${dotClasses[tone]}`} />
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
            {title}
          </p>
        </div>
        <p className="mt-2 truncate text-lg font-semibold text-charcoal dark:text-gray-100">{value}</p>
        <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{detail}</p>
      </div>
    </div>
  );
}

function DraftComparisonPanel({
  draftDelta,
  activePack,
  compact = false,
}: {
  draftDelta?: SemanticContextPackDeltaSummary | null;
  activePack?: SemanticContextPackSummary | null;
  compact?: boolean;
}) {
  const draftChangeCount = getDeltaTotal(draftDelta);
  if (draftChangeCount > 0) {
    return (
      <RuntimeDeltaPanel
        title="Changes to review"
        delta={draftDelta}
        compact={compact}
      />
    );
  }
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-4 dark:border-emerald-900/50 dark:bg-emerald-900/10">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-charcoal dark:text-gray-100">Current draft matches live version</p>
          <p className="mt-1 text-xs leading-5 text-emerald-800 dark:text-emerald-200">
            No unpublished ontology or verified-query changes are reported against {activePack ? `pack-v${activePack.version}` : 'the live Runtime version'}.
          </p>
        </div>
        <Badge variant="success">No changes</Badge>
      </div>
    </div>
  );
}

function RuntimeStatusStrip({
  activePack,
  candidatePack,
  draftChangeCount,
  publishingPackId,
  onPublishPack,
}: {
  activePack?: SemanticContextPackSummary | null;
  candidatePack?: SemanticContextPackSummary | null;
  draftChangeCount: number;
  publishingPackId?: number | null;
  onPublishPack?: (packId: number) => void | Promise<void>;
}) {
  return (
    <div className="border-b border-gray-200 px-5 py-5 dark:border-dark-border">
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
        <div className="border-b border-gray-200 px-5 py-4 dark:border-dark-border">
          <p className="text-sm font-semibold text-charcoal dark:text-gray-100">Runtime status</p>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-stretch">
          <RuntimeStatusNode
            title="Live now"
            value={activePack ? `pack-v${activePack.version}` : 'None published'}
            detail={activePack ? `Active since ${formatDate(activePack.compiled_at)}` : 'Runtime consumers are gated.'}
            tone={activePack ? 'success' : 'neutral'}
            icon={<CircleStackIcon className="h-6 w-6" />}
          />
          <div className="hidden items-center text-gray-400 lg:flex">
            <ChevronRightIcon className="h-5 w-5" />
          </div>
          <RuntimeStatusNode
            title="Draft changes"
            value={draftChangeCount > 0 ? `${draftChangeCount} pending change${draftChangeCount === 1 ? '' : 's'}` : 'No draft changes'}
            detail={draftChangeCount > 0 ? 'Review draft changes below.' : 'Draft matches the live version.'}
            tone={draftChangeCount > 0 ? 'warning' : 'neutral'}
            icon={<ListBulletIcon className="h-6 w-6" />}
          />
          <div className="hidden items-center text-gray-400 lg:flex">
            <ChevronRightIcon className="h-5 w-5" />
          </div>
          <RuntimeStatusNode
            title="Ready to publish"
            value={candidatePack ? `pack-v${candidatePack.version}` : 'No candidate'}
            detail={candidatePack ? `Generated ${formatDate(candidatePack.compiled_at)}` : 'Review changes to prepare one.'}
            tone={candidatePack ? 'brand' : 'neutral'}
            icon={<CubeIcon className="h-6 w-6" />}
          />
          <div className="border-t border-gray-200 px-5 py-5 dark:border-dark-border lg:w-72 lg:border-l lg:border-t-0">
            {candidatePack && onPublishPack ? (
              <Button
                size="sm"
                className="w-full justify-center"
                onClick={() => onPublishPack(candidatePack.id)}
                disabled={publishingPackId === candidatePack.id}
              >
                {publishingPackId === candidatePack.id ? <Spinner size="sm" /> : <RocketLaunchIcon className="h-4 w-4" />}
                Publish candidate
              </Button>
            ) : (
              <Button size="sm" variant="outline" className="w-full justify-center" disabled>
                <ClockIcon className="h-4 w-4" />
                Waiting for candidate
              </Button>
            )}
            <p className="mt-3 text-xs leading-5 text-gray-500 dark:text-gray-400">
              {candidatePack
                ? `Publish pack-v${candidatePack.version} to replace the live version.`
                : 'Create a candidate from reviewed ontology changes before publishing.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function PublishChecklist({
  activePack,
  candidatePack,
  draftChangeCount,
  statusLabel,
}: {
  activePack?: SemanticContextPackSummary | null;
  candidatePack?: SemanticContextPackSummary | null;
  draftChangeCount: number;
  statusLabel: string;
}) {
  const runtimeReady = Boolean(activePack);
  const candidateReady = Boolean(candidatePack);
  const changesReviewed = draftChangeCount === 0 || candidateReady;
  const rows = [
    {
      label: 'Runtime gate',
      detail: runtimeReady ? 'Runtime is using the live version.' : 'Publish a version before runtime queries can execute.',
      ready: runtimeReady,
    },
    {
      label: 'Candidate ready',
      detail: candidateReady ? 'Draft changes prepared and validated.' : 'Save draft, then review changes from the ontology page.',
      ready: candidateReady,
    },
    {
      label: 'Review changes',
      detail: draftChangeCount > 0 ? `Review the ${draftChangeCount} pending change${draftChangeCount === 1 ? '' : 's'} before publishing.` : 'The saved draft matches the live version.',
      ready: changesReviewed,
    },
  ];

  return (
    <aside className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
      <div className="border-b border-gray-200 px-4 py-4 dark:border-dark-border">
        <p className="text-sm font-semibold text-charcoal dark:text-gray-100">Publish checklist</p>
        <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
          {candidateReady ? 'All set to publish.' : statusLabel}
        </p>
      </div>
      <div className="space-y-4 px-4 py-4">
        {rows.map((row) => (
          <div key={row.label} className="flex gap-3">
            {row.ready ? (
              <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
                <CheckCircleIcon className="h-4 w-4" />
              </span>
            ) : (
              <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-amber-300 bg-amber-50 text-amber-700">
                <ClockIcon className="h-3.5 w-3.5" />
              </span>
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-charcoal dark:text-gray-100">{row.label}</p>
              <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{row.detail}</p>
            </div>
          </div>
        ))}
        <div className="flex gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs leading-5 text-sky-800 dark:border-sky-900/40 dark:bg-sky-900/10 dark:text-sky-200">
          <InformationCircleIcon className="h-4 w-4 flex-shrink-0" />
          <span>Save draft on the ontology page, then use Review changes to prepare a candidate. Publishing makes the selected candidate live.</span>
        </div>
      </div>
    </aside>
  );
}

function VersionHistory({
  packs,
  activePackId,
  currentCandidatePackId,
  publishingPackId,
  onPublishPack,
}: {
  packs: SemanticContextPackSummary[];
  activePackId?: number | null;
  currentCandidatePackId?: number | null;
  publishingPackId?: number | null;
  onPublishPack?: (packId: number) => void | Promise<void>;
}) {
  const [expandedPackIds, setExpandedPackIds] = React.useState<Set<number>>(() => new Set([packs[0]?.id].filter(Boolean) as number[]));
  const toggleExpanded = (packId: number) => {
    setExpandedPackIds((current) => {
      const next = new Set(current);
      if (next.has(packId)) {
        next.delete(packId);
      } else {
        next.add(packId);
      }
      return next;
    });
  };

  React.useEffect(() => {
    if (packs.length === 0) return;
    setExpandedPackIds((current) => {
      if (current.size > 0) return current;
      return new Set([packs[0].id]);
    });
  }, [packs]);

  if (packs.length === 0) return null;

  return (
    <div className="border-t border-gray-200 px-5 py-5 dark:border-dark-border">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-charcoal dark:text-gray-100">Version history</p>
          <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
            Generated runtime versions stay reviewable until one is published as live.
          </p>
        </div>
        <Badge variant="secondary">{packs.length} versions</Badge>
      </div>
      <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
        {packs.map((pack) => {
          const isCandidate = pack.status === 'candidate' && !pack.is_active;
          const canPublishCandidate = isCandidate && pack.id === currentCandidatePackId;
          const isExpanded = expandedPackIds.has(pack.id);
          const delta = getDeltaTotal(pack.delta_from_active) > 0
            ? pack.delta_from_active
            : pack.delta_from_previous;
          return (
            <article key={pack.id} className="border-b border-gray-200 last:border-b-0 dark:border-dark-border">
              <div className="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
                <button
                  type="button"
                  onClick={() => toggleExpanded(pack.id)}
                  className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-left transition hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 dark:hover:text-brand-300"
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? (
                    <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-500" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-gray-500" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-3">
                      <span className="font-medium text-charcoal dark:text-gray-100">Runtime version v{pack.version}</span>
                      {packBadge(pack)}
                      {activePackId === pack.id ? <Badge variant="success">Currently live</Badge> : null}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-gray-500 dark:text-gray-400">
                      Generated {formatDate(pack.compiled_at)} · {pack.compiled_by_user_id ? `User ${pack.compiled_by_user_id}` : 'Unknown user'}
                    </span>
                  </span>
                </button>
                <div className="flex items-center gap-2 pl-7 lg:pl-0">
                  {canPublishCandidate && onPublishPack ? (
                    <Button
                      size="sm"
                      onClick={() => onPublishPack(pack.id)}
                      disabled={publishingPackId === pack.id}
                    >
                      {publishingPackId === pack.id ? <Spinner size="sm" /> : <RocketLaunchIcon className="h-4 w-4" />}
                      Publish
                    </Button>
                  ) : null}
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400" aria-hidden="true">
                    <EllipsisVerticalIcon className="h-4 w-4" />
                  </span>
                </div>
              </div>
              {pack.failure_reason ? (
                <p className="px-11 pb-3 text-xs leading-5 text-amber-700 dark:text-amber-200">{pack.failure_reason}</p>
              ) : null}
              {isExpanded ? (
                <div className="border-t border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2/40">
                  <RuntimeDeltaPanel
                    title={getDeltaTotal(pack.delta_from_active) > 0 ? 'Changes from live version' : 'Changes from previous version'}
                    delta={delta}
                    emptyDetail="No material delta was reported for this version."
                  />
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
    </div>
  );
}

export function WorkspaceContextPacksSection({
  workspaceId,
  draft,
  semanticContext,
  isCompiling = false,
  compileError = null,
  compileSuccess = null,
  publishingPackId = null,
  onPublishPack,
}: WorkspaceContextPacksSectionProps) {
  const readiness = semanticContext?.readiness;
  const resolvedWorkspaceId = workspaceId ?? readiness?.workspace_id ?? null;
  const correctionProposalsQuery = useWorkspaceSemanticCorrectionProposals(resolvedWorkspaceId);
  const runtimePacksQuery = useWorkspaceSemanticRuntimePacks(resolvedWorkspaceId);
  const activePack = readiness?.active_pack ?? null;
  const latestPack = readiness?.latest_pack ?? null;
  const candidatePack = latestPack && !latestPack.is_active && latestPack.status !== 'active' ? latestPack : null;
  const draftDelta = readiness?.draft_delta ?? null;
  const draftChangeCount = getDeltaTotal(draftDelta);
  const activePackStaleInfo = getPackStaleInfo(activePack);
  const packDelta = resolvePackDelta(
    activePackStaleInfo,
    correctionProposalsQuery.data?.proposals ?? [],
  );
  const describedAssets = readiness?.catalog_link_count ?? 0;
  const selectedTables = readiness?.selected_table_count ?? draft.selectedAssetCount;
  const ontologyEntityCount = readiness?.ontology_entity_count ?? draft.ontology.entities.length;
  const ontologyMetricCount = readiness?.ontology_metric_count ?? draft.ontology.metrics.length;
  const ontologyRelationshipCount = readiness?.ontology_relationship_count ?? draft.ontology.joins.length;
  const blockingIssues = draft.blockers.filter(
    (blocker) =>
      blocker.severity === 'error' &&
      blocker.code !== 'active_pack_missing' &&
      !String(blocker.code || '').startsWith('catalog_enrichment_'),
  );
  const advisoryIssues = draft.blockers.filter((blocker) => !blockingIssues.includes(blocker));
  const hasAnyPack = Boolean(activePack || latestPack);
  const statusLabel = readiness?.status ?? draft.readiness;
  const runtimePacks = runtimePacksQuery.data?.packs ?? [];

  return (
    <section
      id="runtime-context-packs"
      className="mb-6 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-[hsl(220_25%_18%)] dark:bg-[hsl(217_33%_12%)]"
    >
      <div className="flex flex-col gap-4 border-b border-gray-200 px-5 py-5 dark:border-dark-border lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 gap-4">
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-surface-2">
            <ArchiveBoxIcon className="h-7 w-7 text-eliza-red" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              Runtime versions
              <Badge variant="secondary">Backend state</Badge>
            </div>
            <h2 className="mt-1 font-subtitle text-h2 leading-tight text-charcoal dark:text-white">
              Live and candidate versions
            </h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
              Review saved ontology changes against the live runtime version, then publish the current candidate when it
              should serve Text-to-SQL and MCP.
            </p>
          </div>
        </div>
        <div className="flex flex-col items-start gap-2 lg:items-end">
          <Badge variant={candidatePack ? 'warning' : draftChangeCount > 0 ? 'secondary' : 'success'}>
            {candidatePack ? 'Candidate ready' : draftChangeCount > 0 ? 'Draft pending' : 'Synced'}
          </Badge>
          <p className="max-w-xs text-left text-xs leading-5 text-gray-500 dark:text-gray-400 lg:text-right">
            Runtime candidates are prepared from the ontology Review changes action after saving a draft.
          </p>
        </div>
      </div>

      {isCompiling && (
        <div className="flex items-center gap-2 border-b border-sky-200 bg-sky-50 px-5 py-3 text-sm text-sky-800 dark:border-sky-900/40 dark:bg-sky-900/10 dark:text-sky-200">
          <Spinner size="sm" />
          Creating a candidate Runtime Context version...
        </div>
      )}
      {compileError && (
        <div role="alert" className="border-b border-rose-200 bg-rose-50 px-5 py-3 text-sm text-rose-800 dark:border-rose-900/40 dark:bg-rose-900/10 dark:text-rose-200">
          {compileError}
        </div>
      )}
      {compileSuccess && (
        <div className="border-b border-emerald-200 bg-emerald-50 px-5 py-3 text-sm text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-900/10 dark:text-emerald-200">
          {compileSuccess}
        </div>
      )}

      {!semanticContext && (
        <div className="border-b border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-200">
          Backend lifecycle state is unavailable for this route. Runtime pack metadata is not shown from fallback config.
        </div>
      )}

      <RuntimeStatusStrip
        activePack={activePack}
        candidatePack={candidatePack}
        draftChangeCount={draftChangeCount}
        publishingPackId={publishingPackId}
        onPublishPack={onPublishPack}
      />

      <div className="grid gap-5 border-b border-gray-200 px-5 py-5 dark:border-dark-border lg:grid-cols-[minmax(0,1fr)_360px]">
        <div>
          <DraftComparisonPanel
            draftDelta={draftDelta}
            activePack={activePack}
            compact
          />
        </div>

        <PublishChecklist
          activePack={activePack}
          candidatePack={candidatePack}
          draftChangeCount={draftChangeCount}
          statusLabel={statusLabel}
        />
      </div>

      {activePackStaleInfo.isStale && (
        <PendingPackDelta
          delta={packDelta}
          isLoading={correctionProposalsQuery.isLoading}
          isError={correctionProposalsQuery.isError}
        />
      )}

      {!hasAnyPack && (
        <div className="border-t border-gray-200 px-5 py-5 dark:border-dark-border">
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-5 dark:border-dark-border dark:bg-dark-surface-2/50">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-charcoal dark:text-gray-100">No Runtime Context Pack yet</p>
                <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-500 dark:text-gray-400">
                  Generate once warehouse scope and ontology are configured. Until then, Text-to-SQL and MCP structured
                  access remain unavailable for this workspace.
                </p>
              </div>
              <Badge variant="secondary">Empty</Badge>
            </div>
          </div>
        </div>
      )}

      <RuntimeMetricsBar
        metrics={[
          {
            label: 'Tables',
            value: selectedTables,
            detail: 'Selected warehouse assets in scope',
            icon: <TableCellsIcon className="h-5 w-5" />,
          },
          {
            label: 'Ontology',
            value: `${ontologyEntityCount + ontologyMetricCount}`,
            detail: `${ontologyEntityCount} entities, ${ontologyMetricCount} metrics, ${ontologyRelationshipCount} relationships`,
            icon: <ListBulletIcon className="h-5 w-5" />,
          },
          {
            label: 'Metadata coverage',
            value: selectedTables > 0 ? `${Math.round((describedAssets / selectedTables) * 100)}%` : '0%',
            detail: `${describedAssets} of ${selectedTables} selected assets described`,
            icon: <ShieldCheckIcon className="h-5 w-5" />,
          },
          {
            label: 'Latest generated',
            value: latestPack?.compiled_at ? formatDate(latestPack.compiled_at) : 'Unavailable',
            detail: latestPack?.compiled_at ? 'Reported by backend pack metadata' : 'Backend did not report generation timing',
            icon: <CalendarDaysIcon className="h-5 w-5" />,
          },
        ]}
      />

      {runtimePacksQuery.isLoading ? (
        <div className="border-t border-gray-200 px-5 py-5 text-sm text-gray-500 dark:border-dark-border dark:text-gray-400">
          Loading version history...
        </div>
      ) : (
        <VersionHistory
          packs={runtimePacks}
          activePackId={activePack?.id ?? null}
          currentCandidatePackId={candidatePack?.id ?? null}
          publishingPackId={publishingPackId}
          onPublishPack={onPublishPack}
        />
      )}

      {(blockingIssues.length > 0 || advisoryIssues.length > 0) && (
        <div className="border-t border-gray-200 px-5 py-5 dark:border-dark-border">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2/50">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-charcoal dark:text-gray-100">Readiness blockers</p>
                <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                  These come from semantic readiness and configuration state. Metadata coverage warnings do not block
                  MVP compilation.
                </p>
              </div>
              <Badge variant={blockingIssues.length > 0 ? 'danger' : 'warning'}>
                {blockingIssues.length > 0 ? `${blockingIssues.length} blocking` : `${advisoryIssues.length} advisory`}
              </Badge>
            </div>
            <div className="mt-4 space-y-2">
              {[...blockingIssues, ...advisoryIssues].map((blocker, index) => (
                <div
                  key={`${blocker.code ?? 'blocker'}-${index}`}
                  className="flex gap-2 rounded-lg border border-gray-200 bg-white p-3 text-xs dark:border-dark-border dark:bg-dark-surface"
                >
                  {blocker.severity === 'error' ? (
                    <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 text-rose-500" />
                  ) : (
                    <ClockIcon className="h-4 w-4 flex-shrink-0 text-amber-500" />
                  )}
                  <div className="min-w-0">
                    <p className="font-medium text-charcoal dark:text-gray-100">{blocker.message}</p>
                    {blocker.code && (
                      <p className="mt-1 font-mono text-[11px] text-gray-500 dark:text-gray-400">{blocker.code}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
